/**
 * Imperative i18n for snackbars fired OUTSIDE a React component — plain libs
 * (`lib/wallet/copy-address.ts`), SDK provider callbacks (`onLogoutStart`,
 * rate-limit handler), impersonation teardown, etc. — where `useTranslations`
 * can't be called.
 *
 * `<ToastTranslatorBridge>` (mounted inside the locale tree next to
 * `MutationErrorListener`) registers the live root translator here on mount;
 * imperative call sites then resolve keys via `translateToast`. By the time a
 * user can trigger any of these toasts the app is mounted and the translator
 * is set, so the pre-mount fallback (return the key) is effectively never hit.
 *
 * Component / hook call sites should keep using `useTranslations` directly —
 * this is only for the contexts where that isn't possible.
 */

import type { DynamicTranslator } from '@/lib/errors/resolve-api-error';

let current: DynamicTranslator | null = null;

export function setToastTranslator(t: DynamicTranslator | null): void {
  current = t;
}

/**
 * Translate `key` (a full dotted i18n key) for an imperative snackbar.
 * Returns `fallback` (or the key itself) if the translator isn't mounted yet
 * or the key is missing — never throws.
 */
export function translateToast(
  key: string,
  values?: Record<string, string | number>,
  fallback?: string,
): string {
  const t = current;
  if (t === null) return fallback ?? key;
  try {
    if (!t.has(key)) return fallback ?? key;
    return t(key, values);
  } catch {
    // SILENT-CATCH: next-intl formatter error on an imperative toast — degrade
    // to the fallback string (or raw key) so the snackbar never throws.
    return fallback ?? key;
  }
}
